// Lightweight CSS-grid heatmap for dense matrices (per-cell temperature/SoC
// across racks, block × day price grids). Recharts has no native heatmap, so
// this is a small, dependency-free component with a configurable colour scale.

import { memo, useMemo, useRef } from "react";

import { useHScrollFade } from "@/app/console/useHScroll";

export interface HeatmapCell {
  x: number;
  y: number;
  value: number;
  label?: string;
}

export interface ColorStop {
  at: number; // 0..1
  color: string; // hex
}

export interface HeatmapProps {
  cells: HeatmapCell[];
  cols: number;
  rows: number;
  domain?: [number, number];
  stops?: ColorStop[];
  unit?: string;
  xLabel?: (x: number) => string;
  yLabel?: (y: number) => string;
  gap?: number;
  /** Optional: invoked with the cell when a tile is clicked. When set, tiles
   * become buttons (keyboard-focusable + cursor-pointer). Empty tiles are inert. */
  onCellClick?: (cell: HeatmapCell) => void;
}

// blue → cyan → lime → amber → red (cool to hot)
const DEFAULT_STOPS: ColorStop[] = [
  { at: 0, color: "#1e3a8a" },
  { at: 0.35, color: "#0891b2" },
  { at: 0.6, color: "#8DB85C" },
  { at: 0.8, color: "#f59e0b" },
  { at: 1, color: "#ef4444" },
];

function hexToRgb(hex: string): [number, number, number] {
  const h = hex.replace("#", "");
  return [parseInt(h.slice(0, 2), 16), parseInt(h.slice(2, 4), 16), parseInt(h.slice(4, 6), 16)];
}

function colorAt(t: number, stops: ColorStop[]): string {
  const clamped = Math.max(0, Math.min(1, t));
  let lo = stops[0]!;
  let hi = stops[stops.length - 1]!;
  for (let i = 0; i < stops.length - 1; i++) {
    if (clamped >= stops[i]!.at && clamped <= stops[i + 1]!.at) {
      lo = stops[i]!;
      hi = stops[i + 1]!;
      break;
    }
  }
  const span = hi.at - lo.at || 1;
  const f = (clamped - lo.at) / span;
  const [r1, g1, b1] = hexToRgb(lo.color);
  const [r2, g2, b2] = hexToRgb(hi.color);
  const r = Math.round(r1 + (r2 - r1) * f);
  const g = Math.round(g1 + (g2 - g1) * f);
  const b = Math.round(b1 + (b2 - b1) * f);
  return `rgb(${r}, ${g}, ${b})`;
}

function HeatmapBase({
  cells,
  cols,
  rows,
  domain,
  stops = DEFAULT_STOPS,
  unit = "",
  xLabel,
  yLabel,
  gap = 2,
  onCellClick,
}: HeatmapProps) {
  const [min, max] = useMemo(() => {
    if (domain) return domain;
    const vs = cells.map((c) => c.value);
    return [Math.min(...vs), Math.max(...vs)] as [number, number];
  }, [cells, domain]);

  const grid = useMemo(() => {
    const map = new Map<string, HeatmapCell>();
    for (const c of cells) map.set(`${c.y}:${c.x}`, c);
    return map;
  }, [cells]);

  const range = max - min || 1;
  // Wide grids swipe sideways on phones behind an edge fade (only while clipped).
  const scrollRef = useRef<HTMLDivElement>(null);
  useHScrollFade(scrollRef);

  return (
    <div className="space-y-2">
      <div ref={scrollRef} className="pb-hscroll">
        <div className="inline-grid" style={{ gap, gridTemplateColumns: `auto repeat(${cols}, minmax(14px, 1fr))` }}>
          {/* header row */}
          <div />
          {Array.from({ length: cols }).map((_, x) => (
            <div key={`h${x}`} className="pb-1 text-center text-[9px] text-zinc-600">
              {xLabel ? xLabel(x) : x % 4 === 0 ? x : ""}
            </div>
          ))}
          {/* rows */}
          {Array.from({ length: rows }).map((_, y) => (
            <Row key={`r${y}`}>
              <div className="pr-2 text-right text-[10px] leading-[16px] text-zinc-500">
                {yLabel ? yLabel(y) : `R${y + 1}`}
              </div>
              {Array.from({ length: cols }).map((_, x) => {
                const cell = grid.get(`${y}:${x}`);
                const t = cell ? (cell.value - min) / range : 0;
                const title = cell ? `${cell.label ?? `R${y + 1}·C${x + 1}`}: ${cell.value}${unit}` : "";
                const clickable = !!cell && !!onCellClick;
                if (clickable) {
                  return (
                    <button
                      key={`${y}:${x}`}
                      type="button"
                      title={title}
                      aria-label={title}
                      onClick={() => onCellClick!(cell)}
                      className="h-4 cursor-pointer rounded-[2px] outline-none transition-[box-shadow] hover:ring-1 hover:ring-white/40 focus-visible:ring-2 focus-visible:ring-[#CCFF00]"
                      style={{ backgroundColor: colorAt(t, stops) }}
                    />
                  );
                }
                return (
                  <div
                    key={`${y}:${x}`}
                    title={title}
                    className="h-4 rounded-[2px]"
                    style={{ backgroundColor: cell ? colorAt(t, stops) : "#141414" }}
                  />
                );
              })}
            </Row>
          ))}
        </div>
      </div>

      {/* legend */}
      <div className="flex items-center gap-2 text-[10px] text-zinc-500">
        <span>{min.toFixed(0)}{unit}</span>
        <span
          className="h-2 w-32 rounded-full"
          style={{
            background: `linear-gradient(90deg, ${stops.map((s) => `${s.color} ${s.at * 100}%`).join(", ")})`,
          }}
        />
        <span>{max.toFixed(0)}{unit}</span>
      </div>
    </div>
  );
}

// `display: contents` so each row's cells join the parent grid.
function Row({ children }: { children: React.ReactNode }) {
  return <div style={{ display: "contents" }}>{children}</div>;
}

export const Heatmap = memo(HeatmapBase);
